import { AppBuilderAPI } from '@makyoapp/makyo-sdk'

import type { IAppClient } from '../app/app'
import { AppOverlay } from '../app/overlay'
import { AppPage } from '../app/page'
import { updateWithTimeout } from '../utils'
import type { UndoableAction, UndoStackSubscriber } from './types'

const MAX_UNDO_STACK_SIZE = 100

/**
 * Global undo/redo manager for the app builder
 * Manages a single undo/redo stack across all app operations
 */
export class UndoManager {
  private undoStack: UndoableAction[] = []
  private redoStack: UndoableAction[] = []
  private subscribers: Set<UndoStackSubscriber> = new Set()
  private appClient: IAppClient

  // Coalescing settings
  private coalescingDelay = 500 // ms - actions within this window can be merged

  constructor(appClient: IAppClient) {
    this.appClient = appClient
  }

  /**
   * Execute an action and add it to the undo stack
   */
  execute(action: UndoableAction): void {
    // Try to coalesce with previous action if possible
    if (action.canCoalesce && this.tryCoalesce(action)) {
      this.notifySubscribers()
      return
    }

    // Add to undo stack
    this.undoStack.push(action)

    // Clear redo stack when new action is performed
    this.redoStack = []

    // Limit stack size to prevent memory issues
    if (this.undoStack.length > MAX_UNDO_STACK_SIZE) {
      this.undoStack.shift()
    }

    this.notifySubscribers()
  }

  /**
   * Try to merge this action with the previous one
   */
  private tryCoalesce(action: UndoableAction): boolean {
    const lastAction = this.undoStack[this.undoStack.length - 1]

    if (!lastAction || !lastAction.canCoalesce) return false

    // Same coalescing key and within time window
    if (lastAction.coalescingKey === action.coalescingKey && Date.now() - lastAction.timestamp < this.coalescingDelay) {
      // Update the last action's "after" state and timestamp
      lastAction.after = action.after
      lastAction.timestamp = action.timestamp
      lastAction.description = action.description // Update description to latest

      return true
    }

    return false
  }

  /**
   * Undo the last action
   */
  async undo(): Promise<void> {
    const action = this.undoStack.pop()
    if (!action) return

    try {
      // Apply the reverse change
      await this.applyAction(action, 'undo')

      // Move to redo stack
      this.redoStack.push(action)

      this.notifySubscribers()
    } catch (error) {
      // If undo fails, put the action back
      this.undoStack.push(action)
      console.error('Undo failed:', error)
      throw error
    }
  }

  /**
   * Redo the last undone action
   */
  async redo(): Promise<void> {
    const action = this.redoStack.pop()
    if (!action) return

    try {
      // Reapply the change
      await this.applyAction(action, 'redo')

      // Move back to undo stack
      this.undoStack.push(action)

      this.notifySubscribers()
    } catch (error) {
      // If redo fails, put the action back
      this.redoStack.push(action)
      console.error('Redo failed:', error)
      throw error
    }
  }

  /**
   * Apply an action in either undo or redo direction
   *
   * @todo: everytime we delete/add a page/overlay, a new ID is always generated,
   * this could affect existing app builder config such as actions
   */
  private async applyAction(action: UndoableAction, direction: 'undo' | 'redo'): Promise<void> {
    const stateToApply = direction === 'undo' ? action.before : action.after

    // Determine operation type based on before/after states
    const getOperationType = (): 'add' | 'delete' | 'update' => {
      if (action.before === null) return 'add'
      if (action.after === null) return 'delete'
      return 'update'
    }

    const operationType = getOperationType()
    const operationKey = `${direction}-${operationType}` as const

    switch (action.target) {
      case 'page': {
        switch (operationKey) {
          case 'undo-add':
            // Was an add → delete it locally first, then sync
            this.appClient.pagesMap.delete(action.targetId!)
            this.appClient.notifySubscribers('page')

            updateWithTimeout(async () => {
              await this.appClient.provider.deletePage(this.appClient.appID!, action.targetId!)
              this.appClient.RevalidateVersionHistory()
            })
            break
          case 'redo-add':
            // Was an add, redoing → re-add it locally first, then sync
            {
              const tempPage = new AppPage(this.appClient, this.appClient.pagesMap.size, stateToApply)
              this.appClient.pagesMap.set(stateToApply._id, tempPage)
              this.appClient.notifySubscribers('page')

              updateWithTimeout(async () => {
                await this.appClient.provider.addPage(this.appClient.appID!, stateToApply)
                this.appClient.RevalidateVersionHistory()
              })
            }
            break

          case 'undo-delete':
            // Was a delete → re-add it locally first, then sync
            {
              const tempPage = new AppPage(this.appClient, this.appClient.pagesMap.size, stateToApply)
              this.appClient.pagesMap.set(stateToApply._id, tempPage)
              this.appClient.notifySubscribers('page')

              updateWithTimeout(async () => {
                await this.appClient.provider.addPage(this.appClient.appID!, stateToApply)
                this.appClient.RevalidateVersionHistory()
              })
            }
            break
          case 'redo-delete':
            // Was a delete, redoing → delete it locally first, then sync
            this.appClient.pagesMap.delete(action.targetId!)
            this.appClient.notifySubscribers('page')

            updateWithTimeout(async () => {
              await this.appClient.provider.deletePage(this.appClient.appID!, action.targetId!)
              this.appClient.RevalidateVersionHistory()
            })
            break

          case 'undo-update':
          case 'redo-update':
            // Was an update → restore state and sync to backend
            {
              const page = this.appClient.pagesMap.get(action.targetId!)
              if (!page) break
              await page.Update(stateToApply, true, { skipUndo: true })
            }
            break
        }
        break
      }

      case 'overlay': {
        switch (operationKey) {
          case 'undo-add':
            // Was an add → delete it locally first, then sync
            this.appClient.overlaysMap.delete(action.targetId!)
            this.appClient.notifySubscribers('overlay')

            updateWithTimeout(async () => {
              await this.appClient.provider.deleteOverlay(this.appClient.appID!, action.targetId!)
              this.appClient.RevalidateVersionHistory()
            })
            break

          case 'redo-add':
            // Was an add, redoing → re-add it locally first, then sync
            {
              const tempOverlay = new AppOverlay(this.appClient, this.appClient.overlaysMap.size, stateToApply)
              this.appClient.overlaysMap.set(stateToApply._id, tempOverlay)
              this.appClient.notifySubscribers('overlay')

              updateWithTimeout(async () => {
                await this.appClient.provider.addOverlay(this.appClient.appID!, stateToApply)
                this.appClient.RevalidateVersionHistory()

                // ? for reference
                // if (response._id !== stateToApply._id) {
                //   // Server generated a new ID - update the map
                //   this.appClient.overlaysMap.delete(stateToApply._id)
                //   this.appClient.overlaysMap.set(
                //     response._id,
                //     new AppOverlay(this.appClient, this.appClient.overlaysMap.size, response)
                //   )
                //   this.appClient.notifySubscribers('overlay')
                //   action.targetId = response._id
                // }
              })
            }
            break

          case 'undo-delete':
            // Was a delete → re-add it locally first, then sync
            {
              const tempOverlay = new AppOverlay(this.appClient, this.appClient.overlaysMap.size, stateToApply)
              this.appClient.overlaysMap.set(stateToApply._id, tempOverlay)
              this.appClient.notifySubscribers('overlay')

              updateWithTimeout(async () => {
                await this.appClient.provider.addOverlay(this.appClient.appID!, stateToApply)
                this.appClient.RevalidateVersionHistory()
              })
            }
            break

          case 'redo-delete':
            // Was a delete, redoing → delete it locally first, then sync
            this.appClient.overlaysMap.delete(action.targetId!)
            this.appClient.notifySubscribers('overlay')

            updateWithTimeout(async () => {
              await this.appClient.provider.deleteOverlay(this.appClient.appID!, action.targetId!)
              this.appClient.RevalidateVersionHistory()
            })
            break

          case 'undo-update':
          case 'redo-update':
            // Was an update → restore state and sync to backend
            {
              const overlay = this.appClient.overlaysMap.get(action.targetId!)
              if (!overlay) break
              await overlay.Update(stateToApply, true, { skipUndo: true })
            }
            break
        }
        break
      }

      case 'dialog': {
        switch (operationKey) {
          case 'undo-add':
            // Was an add → delete it locally first, then sync
            this.appClient.builderSettings!._Dialogs = this.appClient.builderSettings!._Dialogs.filter(
              (d) => d._id !== action.targetId!
            )
            this.appClient.notifySubscribers('dialog')

            updateWithTimeout(async () => {
              await AppBuilderAPI.ReqDeleteDialog(this.appClient.appID!, action.targetId!)
              this.appClient.RevalidateVersionHistory()
            })
            break
          case 'redo-add':
            // Was an add, redoing → re-add it locally first, then sync
            this.appClient.builderSettings!._Dialogs = [...this.appClient.builderSettings!._Dialogs, stateToApply]
            this.appClient.notifySubscribers('dialog')

            updateWithTimeout(async () => {
              await AppBuilderAPI.ReqAddDialog(this.appClient.appID!, stateToApply)
              this.appClient.RevalidateVersionHistory()
            })
            break
          case 'undo-delete':
            // Was a delete → re-add it locally first, then sync
            this.appClient.builderSettings!._Dialogs = [...this.appClient.builderSettings!._Dialogs, stateToApply]
            this.appClient.notifySubscribers('dialog')

            updateWithTimeout(async () => {
              await AppBuilderAPI.ReqAddDialog(this.appClient.appID!, stateToApply)
              this.appClient.RevalidateVersionHistory()
            })
            break
          case 'redo-delete':
            // Was a delete, redoing → delete it locally first, then sync
            this.appClient.builderSettings!._Dialogs = this.appClient.builderSettings!._Dialogs.filter(
              (d) => d._id !== action.targetId!
            )
            this.appClient.notifySubscribers('dialog')

            updateWithTimeout(async () => {
              await AppBuilderAPI.ReqDeleteDialog(this.appClient.appID!, action.targetId!)
              this.appClient.RevalidateVersionHistory()
            })
            break
          case 'undo-update':
          case 'redo-update':
            // Was an update → restore state and sync to backend
            {
              // update dialog state in local state
              this.appClient.builderSettings!._Dialogs = this.appClient.builderSettings!._Dialogs.map((d) =>
                d._id === action.targetId! ? stateToApply : d
              )
              this.appClient.notifySubscribers('dialog')

              updateWithTimeout(async () => {
                await AppBuilderAPI.ReqUpdateDialog(this.appClient.appID!, action.targetId!, stateToApply)
                this.appClient.RevalidateVersionHistory()
              })
            }
            break
        }
        break
      }

      case 'menu': {
        switch (operationKey) {
          case 'undo-add':
            // Was an add → delete it locally first, then sync
            this.appClient.builderSettings!._Menus = this.appClient.builderSettings!._Menus.filter(
              (d) => d._id !== action.targetId!
            )
            this.appClient.notifySubscribers('menu')

            updateWithTimeout(async () => {
              await AppBuilderAPI.ReqDeleteMenu(this.appClient.appID!, action.targetId!)
              this.appClient.RevalidateVersionHistory()
            })
            break
          case 'redo-add':
            // Was an add, redoing → re-add it locally first, then sync
            this.appClient.builderSettings!._Menus = [...this.appClient.builderSettings!._Menus, stateToApply]
            this.appClient.notifySubscribers('menu')

            updateWithTimeout(async () => {
              await AppBuilderAPI.ReqAddMenu(this.appClient.appID!, stateToApply)
              this.appClient.RevalidateVersionHistory()
            })
            break
          case 'undo-delete':
            // Was a delete → re-add it locally first, then sync
            this.appClient.builderSettings!._Menus = [...this.appClient.builderSettings!._Menus, stateToApply]
            this.appClient.notifySubscribers('menu')

            updateWithTimeout(async () => {
              await AppBuilderAPI.ReqAddMenu(this.appClient.appID!, stateToApply)
              this.appClient.RevalidateVersionHistory()
            })
            break
          case 'redo-delete':
            // Was a delete, redoing → delete it locally first, then sync
            this.appClient.builderSettings!._Menus = this.appClient.builderSettings!._Menus.filter(
              (d) => d._id !== action.targetId!
            )
            this.appClient.notifySubscribers('menu')

            updateWithTimeout(async () => {
              await AppBuilderAPI.ReqDeleteMenu(this.appClient.appID!, action.targetId!)
              this.appClient.RevalidateVersionHistory()
            })
            break
          case 'undo-update':
          case 'redo-update':
            // Was an update → restore state and sync to backend
            {
              // update menu state in local state
              this.appClient.builderSettings!._Menus = this.appClient.builderSettings!._Menus.map((m) =>
                m._id === action.targetId! ? stateToApply : m
              )
              this.appClient.notifySubscribers('menu')

              updateWithTimeout(async () => {
                await AppBuilderAPI.ReqUpdateMenu(this.appClient.appID!, action.targetId!, stateToApply)
                this.appClient.RevalidateVersionHistory()
              })
            }
            break
        }
        break
      }

      case 'brandkit': {
        if (stateToApply) {
          this.appClient.activeBrandKit = structuredClone(stateToApply)
          this.appClient.notifySubscribers('brandkit')

          updateWithTimeout(async () => {
            await this.appClient.provider.updateBrandkit(stateToApply._id, {
              _Name: stateToApply._Name,
              _Colors: stateToApply._Colors,
              _FontsFamily: stateToApply._FontsFamily,
            })
          })
        }
        break
      }

      case 'builder-brandkit': {
        if (stateToApply) {
          this.appClient.builderSettings!._Style = structuredClone(stateToApply.style)
          this.appClient.activeBrandKit = structuredClone(stateToApply.activeBrandKit)
          this.appClient.notifySubscribers('brandkit')

          updateWithTimeout(async () => {
            await this.appClient.provider.updateBuilderBrandkit(this.appClient.appID!, stateToApply.style)
          })
        }
        break
      }

      case 'builder-globals': {
        if (stateToApply) {
          this.appClient.builderSettings!._Globals = structuredClone(stateToApply)
          this.appClient.notifySubscribers('globals')

          updateWithTimeout(async () => {
            await this.appClient.provider.updateBuilderGlobals(
              this.appClient.appID!,
              this.appClient.builderSettings!._Globals
            )
            this.appClient.RevalidateVersionHistory()
          })
        }
        break
      }

      case 'project': {
        if (stateToApply) {
          this.appClient.projectSettings = structuredClone(stateToApply)
          this.appClient.notifySubscribers('project')
        }
        break
      }

      default:
        break
    }
  }

  /**
   * Check if undo is available
   */
  canUndo(): boolean {
    return this.undoStack.length > 0
  }

  /**
   * Check if redo is available
   */
  canRedo(): boolean {
    return this.redoStack.length > 0
  }

  /**
   * Get description of the action that will be undone
   */
  getUndoDescription(): string | undefined {
    return this.undoStack[this.undoStack.length - 1]?.description
  }

  /**
   * Get description of the action that will be redone
   */
  getRedoDescription(): string | undefined {
    return this.redoStack[this.redoStack.length - 1]?.description
  }

  /**
   * Get the full undo history
   */
  getHistory(): UndoableAction[] {
    return [...this.undoStack]
  }

  /**
   * Subscribe to undo/redo stack changes
   */
  subscribe(callback: UndoStackSubscriber): () => void {
    this.subscribers.add(callback)

    // Immediately call with current state
    callback(this.undoStack, this.redoStack)

    return () => {
      this.subscribers.delete(callback)
    }
  }

  /**
   * Notify all subscribers of stack changes
   */
  private notifySubscribers(): void {
    this.subscribers.forEach((callback) => {
      callback(this.undoStack, this.redoStack)
    })
  }

  /**
   * Get current stack sizes (useful for debugging)
   */
  getStackSizes(): { undo: number; redo: number } {
    return {
      undo: this.undoStack.length,
      redo: this.redoStack.length,
    }
  }

  /**
   * Clear all undo/redo history
   */
  clear(): void {
    this.undoStack = []
    this.redoStack = []
    this.notifySubscribers()
  }
}
